// ---------------------------------------------------------------------------
// loopDrive — "take me on a pleasant loop" (Morgan's review sweep: the one
// feature the drive-for-pleasure crowd raves about that nobody big offers).
//
// You don't want to GO anywhere — you have an hour and want it to be
// lovely. This module turns (origin, minutes) into a set of waypoints that
// trace a rough circle back to where you started, biased toward the
// curated great roads nearby, then hands the whole thing to the EXISTING
// route pipeline (generate → scoring → three cards → GO). The loop is not
// a new routing engine; it's a well-chosen set of vias for the one we have.
// ---------------------------------------------------------------------------
import type { LngLat } from "@/utils/polyline";
import type { TripPoint } from "@/components/TripSearch";

// ---------------------------------------------------------------------------
// THE ANCHOR STORE IS LOADED ON DEMAND (Aug 20, measured).
//
// This module used to `import { GREAT_DRIVES }` at the top. Index imports
// buildLoopPlan, App imports Index eagerly, so that one line dragged 3.97 MB
// of curated-drive records into the entry bundle - downloaded and parsed
// before first paint, on every visit, for every user. Loops are parked
// (SHOW_LOOP_BUTTON = false, rule 6), so in practice nobody was ever using
// what they were paying to download.
//
// buildLoopPlan stays SYNCHRONOUS - no call site changes, no async ripple
// into the three places Index calls it. The store loads on the first call
// and is cached; that first call anchors on distance alone instead of on a
// curated road, which is a slightly plainer loop, not a broken one. Every
// call after it is exactly as before. Confession logged (rule 17).
// ---------------------------------------------------------------------------
interface GreatDriveAnchor {
  point: [number, number];
  name: string;
  scores: { pleasant: number };
  routeUse?: string;
}
let greatDrivesCache: GreatDriveAnchor[] | null = null;
let greatDrivesLoading = false;

function anchorsOrKickOffLoad(): GreatDriveAnchor[] {
  if (greatDrivesCache) return greatDrivesCache;
  if (!greatDrivesLoading) {
    greatDrivesLoading = true;
    // eslint-disable-next-line no-console
    console.log("[loop] curated anchors not loaded yet — this loop uses distance only, fetching for the next one");
    void import("@/content/greatDrives")
      .then((m) => { greatDrivesCache = m.GREAT_DRIVES as unknown as GreatDriveAnchor[]; })
      .catch(() => { greatDrivesLoading = false; });
  }
  return [];
}

/** Load the curated anchor store up front. Call before offering loops so the
 *  first loop is as good as the second. Safe to call repeatedly. */
export async function preloadLoopAnchors(): Promise<void> {
  if (greatDrivesCache) return;
  try {
    const m = await import("@/content/greatDrives");
    greatDrivesCache = m.GREAT_DRIVES as unknown as GreatDriveAnchor[];
  } catch { /* loops simply anchor on distance */ }
}

export interface LoopPlan {
  dest: TripPoint;
  waypoints: LngLat[];
  detourMeta: { summary: string; waypoints: { label: string; reason?: string }[] };
  /** Minutes the loop was sized for — the UI can label the trip. */
  minutes: number;
}

/** Average PLEASANT pace per mode — backroads with stops for views, a
 *  stroll that pauses at the pond, an easy ride. The route engine reports
 *  the real duration on the cards; this only sizes the circle. */
const LOOP_KMH: Record<string, number> = {
  driving: 48, // ≈ 30 mph
  biking: 14,
  walking: 4.5,
};
/** Smallest sensible loop per mode (km) — below this the "loop" collapses
 *  into a block-circling joke. */
const MIN_LOOP_KM: Record<string, number> = {
  driving: 8,
  biking: 3,
  walking: 1.2,
};

function projectKm(origin: LngLat, bearingDeg: number, distKm: number): LngLat {
  const R = 6371;
  const br = (bearingDeg * Math.PI) / 180;
  const lat1 = (origin[1] * Math.PI) / 180;
  const lng1 = (origin[0] * Math.PI) / 180;
  const d = distKm / R;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br),
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(br) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
  );
  return [((lng2 * 180) / Math.PI + 540) % 360 - 180, (lat2 * 180) / Math.PI];
}

function haversineKm(a: LngLat, b: LngLat): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function bearingDeg(a: LngLat, b: LngLat): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Build a loop plan. The best curated road within reach becomes the far
 * side of the circle (a loop with a HEART, not just geometry); without
 * one nearby, the circle leans away from the previous variant so "try a
 * different loop" actually differs.
 */
export function buildLoopPlan(
  origin: LngLat,
  minutes: number,
  /** 0, 1, 2… rotates the loop for variety on regeneration. */
  variant = 0,
  /** Loops work on foot and by bike too (Morgan) — the circle just shrinks
   *  to the pace. */
  mode: "driving" | "walking" | "biking" = "driving",
  /** FILL THE TIME (Morgan): when the routed loop comes back much shorter
   *  than requested (routers chord between waypoints), the caller retries
   *  once with scale > 1 to inflate the circle. */
  scale = 1,
): LoopPlan {
  const kmh = LOOP_KMH[mode] ?? LOOP_KMH.driving;
  const minKm = MIN_LOOP_KM[mode] ?? MIN_LOOP_KM.driving;
  const loopKm = Math.max(minKm, (minutes / 60) * kmh) * Math.max(1, scale);
  const r = loopKm / (2 * Math.PI); // circle radius that fits the budget

  // The farthest point of the loop sits ~2r out (there and back ≈ loop).
  const reachKm = loopKm * 0.42;

  // Best curated drive within reach — highest pleasant score wins, with a
  // mild preference for closer ones (a great road at the reach limit
  // stretches the loop; one at 60% of reach anchors it comfortably).
  let anchor: { point: LngLat; name: string; score: number } | null = null;
  try {
    const candidates: Array<{ point: LngLat; name: string; score: number }> = [];
    for (const d of anchorsOrKickOffLoad()) {
      if (!Array.isArray(d.point)) continue;
      const dist = haversineKm(origin, d.point);
      if (dist < reachKm * 0.25 || dist > reachKm) continue;
      const closeness = 1 - Math.abs(dist / reachKm - 0.6);
      candidates.push({
        point: d.point,
        name: d.name,
        score: (d.scores?.pleasant ?? 0) * (0.75 + 0.5 * closeness),
      });
    }
    // "Another loop" must actually differ: variant walks down the ranked
    // anchors instead of re-serving the winner forever.
    candidates.sort((a, b) => b.score - a.score);
    if (candidates.length > 0) anchor = candidates[variant % candidates.length];
  } catch { /* curated data unavailable — pure-geometry loop */ }

  // Primary bearing: toward the anchor, else rotated per variant.
  const theta = anchor
    ? bearingDeg(origin, anchor.point)
    : (37 + variant * 137) % 360; // golden-angle spread across variants

  const far: LngLat = anchor ? anchor.point : projectKm(origin, theta, reachKm);
  const farKm = haversineKm(origin, far);
  // Two shoulder waypoints push the outbound and return legs APART so the
  // engine can't collapse the loop into an out-and-back on the same road.
  const spread = Math.max(minKm * 0.3, r * 0.9);
  const w1 = projectKm(origin, (theta - 55 + 360) % 360, Math.min(farKm * 0.55, spread * 2));
  const w3 = projectKm(origin, (theta + 55) % 360, Math.min(farKm * 0.55, spread * 2));

  const label = anchor ? anchor.name : "the far turn";
  return {
    minutes,
    dest: {
      name: "Pleasant Loop",
      place: `~${minutes}-minute loop back to here`,
      coords: origin,
    },
    waypoints: [w1, far, w3],
    detourMeta: {
      summary: anchor
        ? `A ~${minutes}-minute loop out to ${anchor.name} and home again.`
        : `A ~${minutes}-minute loop through the pleasant roads nearby.`,
      waypoints: [
        { label: "Out the quiet way" },
        { label, reason: anchor ? "One of the great roads" : undefined },
        { label: "Home the other way" },
      ],
    },
  };
}

export const LOOP_CHOICES: Array<{ minutes: number; label: string }> = [
  { minutes: 30, label: "Half hour" },
  { minutes: 60, label: "One hour" },
  { minutes: 120, label: "Two hours" },
];
